/**
 * Nutrition Web registration analytics events (Jira-aligned).
 *
 * WARNING — Do not pass PII/PHI into these helpers.
 * Only Jira-approved params are sent. product and platform are injected
 * automatically for every registration event.
 */

import { sanitizePathname, trackEvent } from "./analytics";

const NUTRITION_WEB_CONTEXT = {
  product: "nutrition",
  platform: "web",
};

const FLOW_NAME = "new_registration";

const REGISTRATION_ROUTE_PREFIXES = [
  "/register",
  "/calendly-booking",
  "/booking-confirmation",
];

let lastViewedStepKey = null;

const trackRegistrationEvent = (eventName, params = {}) => {
  trackEvent(eventName, {
    ...NUTRITION_WEB_CONTEXT,
    ...params,
  });
};

export const toAnalyticsPaymentType = (paymentType) => {
  if (paymentType === "insurance") {
    return "insurance";
  }
  if (paymentType === "selfpay" || paymentType === "self_pay") {
    return "self_pay";
  }
  return null;
};

export const isRegistrationAnalyticsPath = (pathname = "") => {
  const path = sanitizePathname(pathname);
  return REGISTRATION_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/**
 * Maps a registration pathname to an allow-listed step_name / step_number.
 * Returns null for routes outside the registration flow.
 */
export const getRegistrationStepFromPath = (pathname = "") => {
  const path = sanitizePathname(pathname);

  if (!isRegistrationAnalyticsPath(path)) {
    return null;
  }

  if (path === "/register") {
    return null;
  }

  if (path === "/register/account" || path.endsWith("/no-rd-available")) {
    return { step_name: "account", step_number: 1 };
  }

  if (path.endsWith("/payment")) {
    return { step_name: "payment", step_number: 2 };
  }

  if (path.includes("/insurance")) {
    return { step_name: "insurance", step_number: 3 };
  }

  if (path.endsWith("/card")) {
    return { step_name: "card_on_file", step_number: 4 };
  }

  if (path.includes("/dietitians")) {
    return { step_name: "provider", step_number: 5 };
  }

  if (
    path.includes("/appointments") ||
    path === "/calendly-booking" ||
    path === "/booking-confirmation"
  ) {
    return { step_name: "booking", step_number: 6 };
  }

  if (path.endsWith("/confirmation") || path.endsWith("/download-app")) {
    return { step_name: "completed", step_number: 7 };
  }

  return null;
};

export const getRegistrationStepNameFromPath = (pathname = "") => {
  const step = getRegistrationStepFromPath(pathname);
  return step?.step_name ?? null;
};

export const trackRegistrationStarted = () => {
  trackRegistrationEvent("registration_started", {
    flow_name: FLOW_NAME,
    step_name: "account",
    step_number: 1,
  });
};

/**
 * Fires once per step transition (deduped by step_name + step_number).
 */
export const trackRegistrationStepViewed = (pathname) => {
  const step = getRegistrationStepFromPath(pathname);
  if (!step) {
    return;
  }

  const stepKey = `${step.step_name}:${step.step_number}`;
  if (lastViewedStepKey === stepKey) {
    return;
  }

  lastViewedStepKey = stepKey;

  trackRegistrationEvent("registration_step_viewed", {
    step_name: step.step_name,
    step_number: step.step_number,
  });
};

export const trackRegistrationStepCompleted = (stepName, stepNumber) => {
  trackRegistrationEvent("registration_step_completed", {
    step_name: stepName,
    step_number: stepNumber,
  });
};

export const trackRegistrationStepBack = (fromStep) => {
  if (!fromStep) {
    return;
  }

  trackRegistrationEvent("registration_step_back", {
    from_step: fromStep,
  });
};

export const trackRegistrationPaymentTypeSelected = (paymentType) => {
  const analyticsPaymentType = toAnalyticsPaymentType(paymentType);
  if (!analyticsPaymentType) {
    return;
  }

  trackRegistrationEvent("registration_payment_type_selected", {
    payment_type: analyticsPaymentType,
  });
};

export const trackRegistrationInsuranceVerified = ({ method, result }) => {
  trackRegistrationEvent("registration_insurance_verified", {
    method,
    result,
  });
};

export const trackRegistrationProviderSelected = (providerId) => {
  if (providerId == null || providerId === "") {
    return;
  }

  trackRegistrationEvent("registration_provider_selected", {
    provider_id: providerId,
  });
};

export const trackRegistrationBookingConfirmed = ({
  paymentType,
  providerId,
}) => {
  const analyticsPaymentType = toAnalyticsPaymentType(paymentType);
  if (!analyticsPaymentType || providerId == null || providerId === "") {
    return;
  }

  trackRegistrationEvent("registration_booking_confirmed", {
    payment_type: analyticsPaymentType,
    provider_id: providerId,
  });
};

export const trackRegistrationNoProviderFallback = () => {
  trackRegistrationEvent("registration_no_provider_fallback");
};

export const trackRegistrationError = ({
  stepName,
  stepNumber,
  errorType = "unknown",
}) => {
  trackRegistrationEvent("registration_error", {
    flow_name: FLOW_NAME,
    step_name: stepName,
    step_number: stepNumber,
    status: "failed",
    error_type: errorType,
  });
};

export const trackProviderAvailabilityChecked = () => {
  trackRegistrationEvent("provider_availability_checked", {
    flow_name: FLOW_NAME,
    step_name: "account",
    step_number: 1,
  });
};

export const trackBookingStarted = () => {
  trackRegistrationEvent("booking_started", {
    flow_name: FLOW_NAME,
    step_name: "booking",
    step_number: 6,
    booking_type: "virtual_initial",
  });
};

export const trackBookingCompleted = () => {
  trackRegistrationEvent("booking_completed", {
    flow_name: FLOW_NAME,
    step_name: "booking",
    step_number: 6,
    status: "success",
    booking_type: "virtual_initial",
  });
};

export const trackRegistrationCompleted = () => {
  trackRegistrationEvent("registration_completed", {
    flow_name: FLOW_NAME,
    step_name: "completed",
    step_number: 7,
    status: "success",
  });
};
